import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { IntellioFormlyWrapperComponent } from './itc-formly-wrapper.component';
import { WrapperChangeDetectNotifierService } from './wrapper-change-detect-notifier.service';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'itc-formly-accordion',
  styleUrls: ['./formly-accordion.component.scss'],
  template: `
    <div [id]="'accordion__' + props.step" (click)="props.onClick(field.key)">
      <mat-expansion-panel
        [expanded]="props.active === true"
        #formAccordion
        class="formly-accordion"
        [disabled]="props.isEmpty"
        >
        <mat-expansion-panel-header class="formly-accordion__header">
          <div>
            <mat-panel-title>
              <div
                [ngClass]="{
                  'formly-accordion__header-content': true,
                  sublevel: props.isSublevel
                }"
                >
                @if (
                  !props.legacyReadOnlyMode &&
                  !field.options.formState.isReadOnlyMode &&
                  props.completed !== null &&
                  props.completed &&
                  !props?.omit_status_icon
                  ) {
                  <mat-icon
                    [style]="props.completed ? 'color: #009D3F' : 'color: gray'"
                    class="formly-accordion__header-content__icon"
                    >
                    check_circle_outline
                  </mat-icon>
                }
                @if (
                  !field.options.formState.isReadOnlyMode &&
                  props.completed !== null &&
                  !props.completed &&
                  !props?.omit_status_icon
                  ) {
                  <mat-icon
                    [style]="!props.completed ? 'color: #d93224' : 'color: gray'"
                    class="formly-accordion__header-content__icon"
                    >
                    highlight_off
                  </mat-icon>
                }
                @if (
                  !field.options.formState.isReadOnlyMode &&
                  props.completed === null
                  ) {
                  <mat-icon
                  [style]="'color: gray'"
                    class="formly-accordion__header-content__icon"
                    >
                    do_not_disturb_on
                  </mat-icon>
                }
                <div>
                  @if (props.step) {
                    <div class="formly-accordion__step">
                      STEP {{ props.step }}
                    </div>
                  }
                  <div
                    [ngClass]="{
                      'formly-accordion__title': true,
                      sublevel: props.isSublevel
                    }"
                    >
                    <span>
                      {{ props.title }}
                    </span>
                    @if (props.diff) {
                      <span
                        class="formly-accordion__show-diff"
                        >{{ props.diff }}</span
                        >
                      }
                    </div>
                  </div>
                </div>
              </mat-panel-title>
            </div>
          </mat-expansion-panel-header>
          <div class="formly-accordion__content">
            <ng-container #fieldComponent></ng-container>
          </div>
        </mat-expansion-panel>
      </div>
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatExpansionModule],
})
export class FormlyAccordionComponent
  extends IntellioFormlyWrapperComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  constructor(
    protected notifierService: WrapperChangeDetectNotifierService,
    protected cd: ChangeDetectorRef
  ) {
    super(notifierService, cd);
  }

  @ViewChild(MatExpansionPanel)
  accordion!: MatExpansionPanel;


  ngAfterViewInit() {
    if (!this.props.onClick) {
      // Set to identity function
      // if not explicitly set
      this.props.onClick = (key: string) => key;
    } else {
      const handler = this.props.onClick;
      // decorate click handler with reference to the accordion
      const func = (panel: MatExpansionPanel) => (key: string) => {
        handler(key, panel, this.cd);
      };

      this.props.onClick = func(this.accordion);
    }
  }

  ngOnInit() {
    if (this.props.backendStatusPath) {
      this.syncBackendStatus();
      this.initChangeDetectSub();
      return;
    }

    this.props.completed = null;
    const ctrl = this.field.formControl;
    if (!ctrl) {
      this.initChangeDetectSub();
      return;
    }

    ctrl.statusChanges
      .pipe(
        startWith(ctrl.status),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        this.syncCompletedStatus(val);
      });
    // Recompute on value edits too, since statusChanges only emits on status transitions.
    this.field.formControl.valueChanges
      .pipe(startWith(this.field.formControl.value), takeUntil(this.destroyed$))
      .subscribe(() => this.syncCompletedStatus());

    // Re-check once after init so conditional wrappers that update validity
    // without emitting status changes are reflected on initial page load.
    setTimeout(() => this.syncCompletedStatus());
    this.initChangeDetectSub();
  }

  private syncBackendStatus() {
    const value = this.resolvePath(
      this.props.backendStatusPath,
      this.rootModel
    );

    this.props.completed = typeof value === 'boolean' ? value : null;
    this.cd.markForCheck();
  }

  private syncCompletedStatus(status = this.field.formControl.status) {
    if (status === 'VALID') {
      this.props.completed =
        this.field.formControl.touched ||
        this.hasRequiredField(this.field)
          ? true
          : null;
    } else if (status === 'INVALID') {
      this.props.completed = this.field.formControl.touched ? false : null;
    }

    this.cd.markForCheck();
  }

  private hasRequiredField(field: FormlyFieldConfig): boolean {
    if (field.props?.required && field.formControl?.enabled) {
      return true;
    }

    return (
      field.fieldGroup?.some((child) => this.hasRequiredField(child)) ??
      false
    );
  }

  private get rootModel(): unknown {
    let root = this.field as FormlyFieldConfig;
    while (root.parent) {
      root = root.parent;
    }

    return root.model;
  }

  private resolvePath(path: string, value: unknown): unknown {
    if (!path) {
      return undefined;
    }

    return path.split('.').reduce<unknown>((current, key) => {
      if (current === null || current === undefined) {
        return undefined;
      }

      if (typeof current !== 'object') {
        return undefined;
      }

      return (current as Record<string, unknown>)[key];
    }, value);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
